'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { createContext } from "../react-context";
import { forwardRef, chakra, useMultiStyleConfig, omitThemingProps } from "../system";
import { cx, dataAttr, callAllHandlers } from "../shared-utils";
import { jsx, jsxs } from 'react/jsx-runtime';
import { useImage } from "../image";
import { cloneElement, useState } from 'react';
import { getValidChildren } from "../react-children-utils"; // src/avatar-context.tsx

var [AvatarStylesProvider, useAvatarStyles] = createContext({
  name: `AvatarStylesContext`,
  hookName: `useAvatarStyles`,
  providerName: "<Avatar/>"
});
var placementMap = {
  "top-start": {
    top: "0",
    insetStart: "0",
    transform: "translate(-25%, -25%)"
  },
  "top-end": {
    top: "0",
    insetEnd: "0",
    transform: "translate(25%, -25%)"
  },
  "bottom-start": {
    bottom: "0",
    insetStart: "0",
    transform: "translate(-25%, 25%)"
  },
  "bottom-end": {
    bottom: "0",
    insetEnd: "0",
    transform: "translate(25%, 25%)"
  }
};
var AvatarBadge = forwardRef(function AvatarBadge2(props, ref) {
  const {
    placement = "bottom-end",
    className
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["placement", "className"]);

  const styles = useAvatarStyles();
  const placementStyles = placementMap[placement];
  const badgeStyles = Object.assign({
    position: "absolute",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }, placementStyles, {}, styles.badge);
  return /* @__PURE__ */jsx(chakra.div, Object.assign({
    ref
  }, rest, {
    className: cx("chakra-avatar__badge", className),
    __css: badgeStyles
  }));
});
AvatarBadge.displayName = "AvatarBadge";

function initials(name) {
  var _a;

  const names = name.split(" ");
  const firstName = (_a = names[0]) != null ? _a : "";
  const lastName = names.length > 1 ? names[names.length - 1] : "";
  return firstName && lastName ? `${firstName.charAt(0)}${lastName.charAt(0)}` : firstName.charAt(0);
}

function AvatarName(props) {
  const {
    name,
    getInitials
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["name", "getInitials"]);

  const styles = useAvatarStyles();
  return /* @__PURE__ */jsx(chakra.div, Object.assign({
    role: "img",
    "aria-label": name
  }, rest, {
    __css: styles.label,
    children: name ? getInitials == null ? void 0 : getInitials(name) : null
  }));
}

AvatarName.displayName = "AvatarName"; // src/generic-avatar-icon.tsx

var GenericAvatarIcon = props => /* @__PURE__ */jsxs(chakra.svg, Object.assign({
  viewBox: "0 0 128 128",
  color: "#fff",
  width: "100%",
  height: "100%",
  className: "chakra-avatar__svg"
}, props, {
  children: [/* @__PURE__ */jsx("path", {
    fill: "currentColor",
    d: "M103,102.1388 C93.094,111.92 79.3504,118 64.1638,118 C48.8056,118 34.9294,111.768 25,101.7892 L25,95.2 C25,86.8096 31.981,80 40.6,80 L87.4,80 C96.019,80 103,86.8096 103,95.2 L103,102.1388 Z"
  }), /* @__PURE__ */jsx("path", {
    fill: "currentColor",
    d: "M63.9961647,24 C51.2938136,24 41,34.2938136 41,46.9961647 C41,59.7061864 51.2938136,70 63.9961647,70 C76.6985159,70 87,59.7061864 87,46.9961647 C87,34.2938136 76.6985159,24 63.9961647,24"
  })]
}));

function AvatarImage(props) {
  const {
    src,
    srcSet,
    onError,
    onLoad,
    getInitials,
    name,
    borderRadius,
    loading,
    iconLabel,
    icon = /* @__PURE__ */jsx(GenericAvatarIcon, {}),
    ignoreFallback,
    referrerPolicy,
    crossOrigin
  } = props;
  const status = useImage({
    src,
    onError,
    crossOrigin,
    ignoreFallback
  });
  const hasLoaded = status === "loaded";
  const showFallback = !src || !hasLoaded;

  if (showFallback) {
    return name ? /* @__PURE__ */jsx(AvatarName, {
      className: "chakra-avatar__initials",
      getInitials,
      name
    }) : /*#__PURE__*/cloneElement(icon, {
      role: "img",
      "aria-label": iconLabel
    });
  }

  return /* @__PURE__ */jsx(chakra.img, {
    src,
    srcSet,
    alt: name,
    onLoad,
    referrerPolicy,
    crossOrigin: crossOrigin != null ? crossOrigin : void 0,
    className: "chakra-avatar__img",
    loading,
    __css: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius
    }
  });
}

AvatarImage.displayName = "AvatarImage";
var baseStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  textTransform: "uppercase",
  fontWeight: "medium",
  position: "relative",
  flexShrink: 0
};
var Avatar = forwardRef((props, ref) => {
  const styles = useMultiStyleConfig("Avatar", props);
  const [isLoaded, setIsLoaded] = useState(false);

  const _omitThemingProps = omitThemingProps(props),
        {
    src,
    srcSet,
    name,
    showBorder,
    borderRadius = "full",
    onError,
    onLoad: onLoadProp,
    getInitials = initials,
    icon = /* @__PURE__ */jsx(GenericAvatarIcon, {}),
    iconLabel = " avatar",
    loading,
    children,
    borderColor,
    ignoreFallback,
    crossOrigin
  } = _omitThemingProps,
        rest = _objectWithoutPropertiesLoose(_omitThemingProps, ["src", "srcSet", "name", "showBorder", "borderRadius", "onError", "onLoad", "getInitials", "icon", "iconLabel", "loading", "children", "borderColor", "ignoreFallback", "crossOrigin"]);

  const avatarStyles = Object.assign({
    borderRadius,
    borderWidth: showBorder ? "2px" : void 0
  }, baseStyle, {}, styles.container);

  if (borderColor) {
    avatarStyles.borderColor = borderColor;
  }

  return /* @__PURE__ */jsx(chakra.span, Object.assign({
    ref
  }, rest, {
    className: cx("chakra-avatar", props.className),
    "data-loaded": dataAttr(isLoaded),
    __css: avatarStyles,
    children: /* @__PURE__ */jsxs(AvatarStylesProvider, {
      value: styles,
      children: [/* @__PURE__ */jsx(AvatarImage, {
        src,
        srcSet,
        loading,
        onLoad: callAllHandlers(onLoadProp, () => {
          setIsLoaded(true);
        }),
        onError,
        getInitials,
        name,
        borderRadius,
        icon,
        iconLabel,
        ignoreFallback,
        crossOrigin
      }), children]
    })
  }));
});
Avatar.displayName = "Avatar"; // ../../utilities/object-utils/src/compact.ts

function compact(object) {
  const clone = Object.assign({}, object);

  for (let key in clone) {
    if (clone[key] === void 0) delete clone[key];
  }

  return clone;
}

var AvatarGroup = forwardRef(function AvatarGroup2(props, ref) {
  const styles = useMultiStyleConfig("Avatar", props);

  const _omitThemingProps2 = omitThemingProps(props),
        {
    children,
    borderColor,
    max,
    spacing = "-0.75rem",
    borderRadius = "full"
  } = _omitThemingProps2,
        rest = _objectWithoutPropertiesLoose(_omitThemingProps2, ["children", "borderColor", "max", "spacing", "borderRadius"]);

  const validChildren = getValidChildren(children);
  const childrenWithinMax = max != null ? validChildren.slice(0, max) : validChildren;
  const excess = max != null ? validChildren.length - max : 0;
  const reversedChildren = childrenWithinMax.reverse();
  const clones = reversedChildren.map((child, index) => {
    var _a;

    const isFirstAvatar = index === 0;
    const childProps = {
      marginEnd: isFirstAvatar ? 0 : spacing,
      size: props.size,
      borderColor: (_a = child.props.borderColor) != null ? _a : borderColor,
      showBorder: true
    };
    return /*#__PURE__*/cloneElement(child, compact(childProps));
  });
  const groupStyles = Object.assign({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexDirection: "row-reverse"
  }, styles.group);
  const excessStyles = Object.assign({
    borderRadius,
    marginStart: spacing
  }, baseStyle, {}, styles.excessLabel);
  return /* @__PURE__ */jsxs(chakra.div, Object.assign({
    ref,
    role: "group",
    __css: groupStyles
  }, rest, {
    className: cx("chakra-avatar__group", props.className),
    children: [excess > 0 && /* @__PURE__ */jsx(chakra.span, {
      className: "chakra-avatar__excess",
      __css: excessStyles,
      children: `+${excess}`
    }), clones]
  }));
});
AvatarGroup.displayName = "AvatarGroup";
export { Avatar, AvatarBadge, AvatarGroup, GenericAvatarIcon, useAvatarStyles };